/**
 * Fi client for trading operations
 * Follows DexClient pattern from pkg/netrunner/clients/dex_client.go
 * Handles EIP-712 signatures and automatic satoshi conversion
 */

import { BaseSignedClient } from "./BaseSignedClient";
import { toSatoshi } from "../../utils/math";
import { fromSatoshi } from "../../utils/math";
import {
  shouldRefreshNonce,
  isDuplicateTransactionError,
} from "../../utils/errorDetection";
import { toRFC3339Nano } from "../../utils/time";
import {
  OpSubmitOrder,
  OpCancelOrder,
  OpModifyOrder,
  OpChangeLeverage,
  OpClosePosition,
  OpSwap,
  OpChangeMarginRatio,
  OpInternTransfer,
  OpBankBucketTransfer,
  OpBankTransferToBucket,
  OpBucketTransferToBank,
  OpBankMint,
  OpBankTransfer,
  OpCreateMarginBucket,
  ExchangeClientConfig,
  PlaceOrderParams,
  CancelOrderParams,
  ModifyOrderParams,
  ChangeLeverageParams,
  ClosePositionParams,
  SwapParams,
  ChangeMarginRatioParams,
  InternTransferParams,
  BucketTransferParams,
  ExtractToBucketParams,
  WithdrawFromBucketParams,
  BankMintParams,
  BankTransferParams,
  CreateBucketParams,
  BatchOrderParams,
  PlaceOrderResponse,
  CancelOrderResponse,
  ModifyOrderResponse,
  ChangeLeverageResponse,
  ClosePositionResponse,
  ChangeMarginRatioResponse,
  InternTransferResponse,
  BucketTransferResponse,
  ExtractToBucketResponse,
  WithdrawFromBucketResponse,
  BankMintResponse,
  BankTransferResponse,
  CreateBucketResponse,
  BatchOrderResponse,
  parseTxV1SubmitResponse,
  TxV1SubmitResponse,
} from "../../types";

export class FiClient extends BaseSignedClient {
  constructor(config: ExchangeClientConfig) {
    super(config);
  }

  protected override getNotInitializedMessage(): string {
    return "FiClient not initialized. Call initialize() first.";
  }

  /**
   * Refresh nonce from server (called on nonce errors). Public for retry logic.
   * @deprecated Nonces are now queried per-transaction by getNextNonce(). Kept for backward compat.
   */
  override async refreshNonceFromServer(): Promise<void> {
    await super.refreshNonceFromServer();
    console.log("[FiClient] Refreshed nonce from server", {
      walletAddress: this.getWalletAddress(),
    });
  }

  /**
   * Build order submission payload following buildOrderSubmissionPayload pattern
   * Creates payload with action, marketId, bucketId, side, orderType, price, quantity
   * Converts price and quantity from decimal numbers to satoshi strings
   */
  private buildOrderPayload(params: PlaceOrderParams): Record<string, any> {
    // Convert price and quantity from decimal numbers to satoshi strings
    const priceStr = toSatoshi(params.price);
    const quantityStr = toSatoshi(params.quantity);

    // Build payload following buildOrderSubmissionPayload pattern
    const payload: Record<string, any> = {
      action: OpSubmitOrder,
      marketId: params.marketId,
      bucketId: params.bucketId, // Required: Bucket ID for order placement
      side: params.side.toUpperCase(),
      orderType: params.orderType.toUpperCase(),
      price: priceStr, // Satoshi format string
      quantity: quantityStr, // Satoshi format string
    };

    // Add optional advanced parameters (all in satoshi format)
    if (params.leverage) {
      payload.leverage = params.leverage;
    }
    if (params.takeProfit) {
      payload.takeProfit = params.takeProfit;
    }
    if (params.stopLoss) {
      payload.stopLoss = params.stopLoss;
    }

    return payload;
  }

  /**
   * EIP-712 payload for POST /tx/v1/submit place-order (clob::submit_order).
   *
   * CRITICAL: All string field values MUST match the canonical normalized forms
   * used by the Go standards library (standards/tx/clob/tx.go) and the morphcore
   * authenticator (morphcore/pkg/modules/auth/keeper/authenticator.go).
   *
   * Canonical forms:
   *   - side:        "buy" | "sell"              (lowercase, matches normalizeSide)
   *   - orderType:   "limit" | "market" | ...    (lowercase short, matches normalizeOrderType)
   *   - fillType:    "any" | "taker" | "maker"   (matches deriveFillType)
   *   - timeInForce: "GTC" | "IOC" | "FOK"       (uppercase, matches normalizeProtoTimeInForce)
   *   - leverage:    "0" when unset               (matches Go E2E default)
   */
  private buildOrderPayloadForTx(params: PlaceOrderParams): Record<string, any> {
    const isMarket = params.orderType.toUpperCase() === "MARKET";
    const priceStr = isMarket ? "0" : toSatoshi(params.price);
    const quantityStr = toSatoshi(params.quantity);
    const timeInForce = (params.timeInForce || "GTC").toUpperCase();
    const marginMode = params.bucketId ? "isolated" : "cross";
    const sltpParts: string[] = [];
    if (params.stopLoss) sltpParts.push(`sl:${params.stopLoss}`);
    if (params.takeProfit) sltpParts.push(`tp:${params.takeProfit}`);
    const sltp = sltpParts.length ? sltpParts.join(",") : "";
    const leverage = params.leverage || "0";

    // Normalize to canonical lowercase forms matching standards/tx/clob/tx.go
    const side = params.side.toLowerCase(); // "buy" | "sell"
    const orderType = this.normalizeOrderType(params.orderType); // "limit" | "market" | ...
    const fillType = this.deriveFillType(orderType, params.postOnly); // "any" | "taker" | "maker"

    return {
      action: OpSubmitOrder,
      address: this.getWalletAddress(),
      marketIndex: params.marketId,
      side,
      orderType,
      price: priceStr,
      stopPrice: "0",
      quantity: quantityStr,
      leverage,
      fillType,
      marginMode,
      bucketId: params.bucketId,
      metadata: "",
      sltp,
      timeInForce,
    };
  }

  /**
   * Normalize order type to canonical lowercase form for EIP-712 signing.
   * Must match standards/tx/clob/tx.go normalizeOrderType().
   */
  private normalizeOrderType(orderType: string): string {
    switch (orderType.toUpperCase()) {
      case "LIMIT":
        return "limit";
      case "MARKET":
        return "market";
      case "STOP_MARKET":
        return "stop_market";
      case "STOP_LIMIT":
        return "stop_limit";
      case "TAKE_PROFIT":
        return "take_profit";
      case "TRAILING_STOP":
        return "trailing_stop";
      default:
        return orderType.toLowerCase();
    }
  }

  /**
   * Derive fill type from order type and post-only flag for EIP-712 signing.
   * Must match standards/tx/clob/tx.go deriveFillType().
   */
  private deriveFillType(orderType: string, postOnly?: boolean): string {
    if (postOnly) {
      return "maker";
    }
    if (orderType === "market") {
      return "taker";
    }
    return "any";
  }

  /**
   * Map canonical TIF string to proto enum value.
   * Must match proto-lib/proto/clob/v1/tx.proto TimeInForce enum.
   */
  private toTimeInForceProto(tif?: string): string {
    switch ((tif || "GTC").toUpperCase()) {
      case "IOC":
        return "TIME_IN_FORCE_IOC";
      case "FOK":
        return "TIME_IN_FORCE_FOK";
      case "DAY":
        return "TIME_IN_FORCE_DAY";
      case "GTT":
      case "GTD":
        return "TIME_IN_FORCE_GTT";
      default:
        return "TIME_IN_FORCE_GTC";
    }
  }

  /**
   * Place order with a pre-reserved nonce.
   *
   * Identical to placeOrder but skips the per-transaction nonce reservation
   * HTTP call.  Designed for high-throughput scenarios where the caller has
   * already reserved a batch of nonces via InfoClient.getNonceBatch().
   *
   * @param params  - Standard order parameters (same as placeOrder)
   * @param nonce   - Pre-reserved nonce (from getNonceBatch)
   * @returns PlaceOrderResponse
   */
  async placeOrderWithNonce(
    params: PlaceOrderParams,
    nonce: number,
  ): Promise<PlaceOrderResponse> {
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }
    if (
      !params.side ||
      (params.side.toUpperCase() !== "BUY" &&
        params.side.toUpperCase() !== "SELL")
    ) {
      throw new Error("side must be BUY or SELL");
    }
    if (
      !params.orderType ||
      (params.orderType.toUpperCase() !== "LIMIT" &&
        params.orderType.toUpperCase() !== "MARKET")
    ) {
      throw new Error("orderType must be LIMIT or MARKET");
    }
    if (!params.price || params.price <= 0) {
      throw new Error("price must be positive");
    }
    if (!params.quantity || params.quantity <= 0) {
      throw new Error("quantity must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    return this.placeOrderViaTxWithNonce(params, nonce);
  }

  /**
   * Internal: place order via /tx/v1/submit with an explicit nonce (no server round-trip).
   */
  private async placeOrderViaTxWithNonce(
    params: PlaceOrderParams,
    nonce: number,
  ): Promise<PlaceOrderResponse> {
    const _t0 = performance.now();
    const walletAddress = this.getWalletAddress();
    const payload = this.buildOrderPayloadForTx(params);
    const signatureBuilder = this.getSignatureBuilder();

    // Build complete message with the explicit nonce (no HTTP call)
    const now = Math.floor(Date.now() / 1000);
    const deadline = now + 3600; // 1 hour
    const completeMessage: Record<string, any> = {
      ...payload,
      owner: walletAddress,
      nonce: String(nonce),
      deadline: String(deadline),
    };

    const _t1 = performance.now();
    const signature = await signatureBuilder.signPayload(completeMessage);
    const _t2 = performance.now();

    const deadlineNum = deadline;
    const deadlineRFC3339 = new Date(deadlineNum * 1000)
      .toISOString()
      .replace(/\.\d{3}Z$/, "Z");
    const timestamp = toRFC3339Nano();
    const priceStr = completeMessage.price as string;
    const quantityStr = completeMessage.quantity as string;
    const orderType =
      params.orderType.toUpperCase() === "MARKET"
        ? "ORDER_TYPE_MARKET"
        : "ORDER_TYPE_LIMIT";
    const side =
      params.side.toUpperCase() === "BUY" ? "SIDE_BUY" : "SIDE_SELL";
    const timeInForceProto = this.toTimeInForceProto(params.timeInForce);

    const clobMessage: Record<string, unknown> = {
      "@type": "/clob.v1.MsgPlaceOrderRequest",
      type: 2,
      fromAddress: walletAddress,
      address: walletAddress,
      marketIndex: parseInt(params.marketId, 10) || params.marketId,
      symbol: params.marketId,
      price: priceStr,
      quantity: quantityStr,
      size: quantityStr,
      side,
      orderType,
      bucketId: params.bucketId,
      timeInForce: timeInForceProto,
      is_buy: params.side.toUpperCase() === "BUY",
      timestamp,
    };
    if (params.leverage) (clobMessage as Record<string, unknown>).leverage = params.leverage;
    if (params.takeProfit) (clobMessage as Record<string, unknown>).takeProfit = params.takeProfit;
    if (params.stopLoss) (clobMessage as Record<string, unknown>).stopLoss = params.stopLoss;
    if (params.postOnly) (clobMessage as Record<string, unknown>).postOnly = true;
    if (params.hidden) (clobMessage as Record<string, unknown>).hidden = true;
    if (params.displayQuantity) (clobMessage as Record<string, unknown>).displayQuantity = params.displayQuantity;

    const sigBase64 = this.hexSignatureToBase64(signature);
    const txRequest = {
      tx: {
        body: {
          messages: [clobMessage],
          memo: `place_order_${Math.floor(Date.now() / 1000)}_${nonce}`,
          timeoutTimestamp: deadlineRFC3339,
        },
        auth_info: {
          signer_infos: [
            {
              nonce,
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    const _t3 = performance.now();
    const response = await this.httpClient.post("/tx/v1/submit", txRequest);
    const _t4 = performance.now();
    console.log(
      `[FiClient][PERF] placeOrderWithNonce nonce=${nonce} | sign: ${(_t2 - _t1).toFixed(1)}ms | http: ${(_t4 - _t3).toFixed(1)}ms | total: ${(_t4 - _t0).toFixed(1)}ms`,
    );

    const body = parseTxV1SubmitResponse(response.body);
    const raw = (response.body ?? {}) as Record<string, unknown>;
    const orderId = raw.orderId != null ? String(raw.orderId) : undefined;

    return {
      success: body.accepted,
      orderId: orderId ?? "",
      errorMessage: body.accepted ? "" : body.errorMessage,
      ...(body.txhash && { txhash: body.txhash }),
    } as PlaceOrderResponse;
  }

  /**
   * Build a fully-signed order request without submitting.
   *
   * Returns the raw HTTP request body ready for submitSignedOrderRequest().
   * This separates CPU-bound signing from I/O-bound submission, enabling
   * two-phase TPS tests where signing is serialized (WASM is single-threaded)
   * and submission is parallelized.
   */
  async buildSignedOrderRequest(
    params: PlaceOrderParams,
    nonce: number,
  ): Promise<Record<string, unknown>> {
    if (!this.walletAddress) await this.initialize();
    const walletAddress = this.getWalletAddress();
    const payload = this.buildOrderPayloadForTx(params);
    const signatureBuilder = this.getSignatureBuilder();

    const now = Math.floor(Date.now() / 1000);
    const deadline = now + 3600;
    const completeMessage: Record<string, any> = {
      ...payload,
      owner: walletAddress,
      nonce: String(nonce),
      deadline: String(deadline),
    };

    const signature = await signatureBuilder.signPayload(completeMessage);

    const deadlineRFC3339 = new Date(deadline * 1000)
      .toISOString()
      .replace(/\.\d{3}Z$/, "Z");
    const timestamp = toRFC3339Nano();
    const priceStr = completeMessage.price as string;
    const quantityStr = completeMessage.quantity as string;
    const orderType =
      params.orderType.toUpperCase() === "MARKET"
        ? "ORDER_TYPE_MARKET"
        : "ORDER_TYPE_LIMIT";
    const side =
      params.side.toUpperCase() === "BUY" ? "SIDE_BUY" : "SIDE_SELL";
    const timeInForceProto = this.toTimeInForceProto(params.timeInForce);

    const clobMessage: Record<string, unknown> = {
      "@type": "/clob.v1.MsgPlaceOrderRequest",
      type: 2,
      fromAddress: walletAddress,
      address: walletAddress,
      marketIndex: parseInt(params.marketId, 10) || params.marketId,
      symbol: params.marketId,
      price: priceStr,
      quantity: quantityStr,
      size: quantityStr,
      side,
      orderType,
      bucketId: params.bucketId,
      timeInForce: timeInForceProto,
      is_buy: params.side.toUpperCase() === "BUY",
      timestamp,
    };
    if (params.leverage)
      (clobMessage as Record<string, unknown>).leverage = params.leverage;
    if (params.takeProfit)
      (clobMessage as Record<string, unknown>).takeProfit = params.takeProfit;
    if (params.stopLoss)
      (clobMessage as Record<string, unknown>).stopLoss = params.stopLoss;
    if (params.postOnly)
      (clobMessage as Record<string, unknown>).postOnly = true;
    if (params.hidden)
      (clobMessage as Record<string, unknown>).hidden = true;
    if (params.displayQuantity)
      (clobMessage as Record<string, unknown>).displayQuantity =
        params.displayQuantity;

    const sigBase64 = this.hexSignatureToBase64(signature);
    return {
      tx: {
        body: {
          messages: [clobMessage],
          memo: `place_order_${Math.floor(Date.now() / 1000)}_${nonce}`,
          timeoutTimestamp: deadlineRFC3339,
        },
        auth_info: {
          signer_infos: [{ nonce, deadline, timestamp }],
        },
        signatures: [sigBase64],
      },
    };
  }

  /**
   * Submit a pre-signed order request (built by buildSignedOrderRequest).
   *
   * This performs only the HTTP POST — no signing, no nonce fetch.
   * Used by TPS tests to measure pure network + server latency.
   */
  async submitSignedOrderRequest(
    txRequest: Record<string, unknown>,
  ): Promise<PlaceOrderResponse> {
    const response = await this.httpClient.post("/tx/v1/submit", txRequest);
    const body = parseTxV1SubmitResponse(response.body);
    const raw = (response.body ?? {}) as Record<string, unknown>;
    const orderId = raw.orderId != null ? String(raw.orderId) : undefined;

    return {
      success: body.accepted,
      orderId: orderId ?? "",
      errorMessage: body.accepted ? "" : body.errorMessage,
      ...(body.txhash && { txhash: body.txhash }),
    } as PlaceOrderResponse;
  }

  /**
   * Submit a batch of pre-signed order requests in a single HTTP request.
   *
   * Uses POST /tx/v1/submit/batch — all transactions are sent in one HTTP
   * round-trip, eliminating the browser's per-host connection limit (~6 for
   * HTTP/1.1) that causes severe queuing latency with individual requests.
   *
   * Each element of signedRequests must be the output of buildSignedOrderRequest().
   * Returns per-transaction results aligned with the input array.
   */
  async submitBatchSignedOrderRequests(
    signedRequests: Record<string, unknown>[],
  ): Promise<PlaceOrderResponse[]> {
    // Extract the inner `tx` objects from each SubmitTxRequest wrapper
    const txs = signedRequests.map((req) => (req as Record<string, any>).tx);

    const batchBody = { txs };
    const response = await this.httpClient.post(
      "/tx/v1/submit/batch",
      batchBody,
    );
    const body = (response.body ?? {}) as Record<string, unknown>;

    // Parse per-tx results from the batch response.
    // The router returns SubmitTxBatchResponse with:
    //   results: TxV1SubmitResponse[] (accepted, txhash, errorMessage per tx)
    //   success: bool (batch-level)
    //   errorMessage: string (batch-level, e.g. from morphcore rejection)
    const rawResults = (body.results ?? []) as unknown[];
    const batchSuccess = body.success === true;
    const batchError = (body.errorMessage as string) ?? "";

    console.log(
      `[FiClient][submitBatch] HTTP ${response.statusCode} | success=${batchSuccess} | results=${rawResults.length} | batchError=${batchError || "(none)"}`,
    );
    if (!batchSuccess && rawResults.length > 0) {
      // Log first result for diagnosis
      console.log("[FiClient][submitBatch] first result:", JSON.stringify(rawResults[0]));
    }

    // If no per-tx results returned, synthesize from batch-level status
    if (rawResults.length === 0) {
      return signedRequests.map(() => ({
        success: batchSuccess,
        orderId: "",
        errorMessage: batchSuccess
          ? ""
          : batchError || "batch rejected with no per-tx details",
      })) as PlaceOrderResponse[];
    }

    return rawResults.map((r) => {
      const parsed = parseTxV1SubmitResponse(r);
      const raw = (r ?? {}) as Record<string, unknown>;
      const orderId = raw.orderId != null ? String(raw.orderId) : undefined;
      const errorMessage =
        parsed.errorMessage || (parsed.accepted ? "" : batchError) || "";
      return {
        success: parsed.accepted,
        orderId: orderId ?? "",
        errorMessage: parsed.accepted ? "" : errorMessage,
        ...(parsed.txhash && { txhash: parsed.txhash }),
      } as PlaceOrderResponse;
    });
  }

  /**
   * Place order via POST /tx/v1/submit (E2E/consensus path). Commits nonce only when accepted === true.
   * On errorMessage containing "duplicate transaction", refreshes nonce and retries once.
   */
  private async placeOrderViaTx(
    params: PlaceOrderParams,
    isRetry = false,
  ): Promise<PlaceOrderResponse> {
    const _t0 = performance.now();
    const walletAddress = this.getWalletAddress();
    const payload = this.buildOrderPayloadForTx(params);
    console.log("[FiClient] EIP-712 signing payload (canonical values):", {
      side: payload.side,
      orderType: payload.orderType,
      fillType: payload.fillType,
      marginMode: payload.marginMode,
      leverage: payload.leverage,
      timeInForce: payload.timeInForce,
      owner: payload.owner ?? "(added by buildCompleteMessage)",
    });
    const signatureBuilder = this.getSignatureBuilder();

    const _t1 = performance.now();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const _t2 = performance.now();

    const signature = await signatureBuilder.signPayload(completeMessage);
    const _t3 = performance.now();

    console.log(`[FiClient][PERF] buildPayload: ${(_t1 - _t0).toFixed(1)}ms | nonceFetch: ${(_t2 - _t1).toFixed(1)}ms | sign: ${(_t3 - _t2).toFixed(1)}ms`);
    const deadlineNum = parseInt(String(completeMessage.deadline), 10);
    const deadlineRFC3339 = new Date(deadlineNum * 1000)
      .toISOString()
      .replace(/\.\d{3}Z$/, "Z");
    const timestamp = toRFC3339Nano();
    const priceStr = completeMessage.price as string;
    const quantityStr = completeMessage.quantity as string;
    const orderType =
      params.orderType.toUpperCase() === "MARKET"
        ? "ORDER_TYPE_MARKET"
        : "ORDER_TYPE_LIMIT";
    const side =
      params.side.toUpperCase() === "BUY" ? "SIDE_BUY" : "SIDE_SELL";
    const timeInForceProto = this.toTimeInForceProto(params.timeInForce);

    const clobMessage: Record<string, unknown> = {
      "@type": "/clob.v1.MsgPlaceOrderRequest",
      type: 2,
      fromAddress: walletAddress,
      address: walletAddress,
      marketIndex: parseInt(params.marketId, 10) || params.marketId,
      symbol: params.marketId,
      price: priceStr,
      quantity: quantityStr,
      size: quantityStr,
      side,
      orderType,
      bucketId: params.bucketId,
      timeInForce: timeInForceProto,
      is_buy: params.side.toUpperCase() === "BUY",
      timestamp,
    };
    if (params.leverage) (clobMessage as Record<string, unknown>).leverage = params.leverage;
    if (params.takeProfit) (clobMessage as Record<string, unknown>).takeProfit = params.takeProfit;
    if (params.stopLoss) (clobMessage as Record<string, unknown>).stopLoss = params.stopLoss;
    if (params.postOnly) (clobMessage as Record<string, unknown>).postOnly = true;
    if (params.reduceOnly) (clobMessage as Record<string, unknown>).reduceOnly = true;
    if (params.hidden) (clobMessage as Record<string, unknown>).hidden = true;
    if (params.displayQuantity) (clobMessage as Record<string, unknown>).displayQuantity = params.displayQuantity;

    const sigBase64 = this.hexSignatureToBase64(signature);
    const txRequest = {
      tx: {
        body: {
          messages: [clobMessage],
          memo: `place_order_${Math.floor(Date.now() / 1000)}_${completeMessage.nonce}`,
          timeoutTimestamp: deadlineRFC3339,
        },
        auth_info: {
          signer_infos: [
            {
              nonce: parseInt(String(completeMessage.nonce), 10),
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    const _t4 = performance.now();
    const response = await this.httpClient.post("/tx/v1/submit", txRequest);
    const _t5 = performance.now();
    console.log(`[FiClient][PERF] httpPost(/tx/v1/submit): ${(_t5 - _t4).toFixed(1)}ms | total: ${(_t5 - _t0).toFixed(1)}ms`);

    const body = parseTxV1SubmitResponse(response.body);
    const raw = (response.body ?? {}) as Record<string, unknown>;
    const orderId = raw.orderId != null ? String(raw.orderId) : undefined;

    // Nonce lifecycle is system-managed: the router ingress keeper commits
    // nonces after successful morphcore execution (SubmitTx → CommitNonce).
    // The client does NOT track or commit nonces locally.

    // Duplicate transaction: retry once with a fresh server-assigned nonce
    if (
      !isRetry &&
      !body.accepted &&
      isDuplicateTransactionError(body.errorMessage) &&
      this.walletAddress
    ) {
      console.log("[FiClient] Duplicate transaction detected, retrying with fresh nonce", payload);
      return this.placeOrderViaTx(params, true);
    }
    console.log("[FiClient] placeOrderViaTx result", {
      accepted: body.accepted,
      nonce: completeMessage.nonce,
      txhash: body.txhash || undefined,
      errorMessage: body.errorMessage || undefined,
    });
    return {
      success: body.accepted,
      orderId: orderId ?? "",
      errorMessage: body.accepted ? "" : body.errorMessage,
      ...(body.txhash && { txhash: body.txhash }),
    } as PlaceOrderResponse;
  }

  /**
   * Legacy place order via POST /clob/v1/order (flat body).
   */
  private async placeOrderLegacy(params: PlaceOrderParams): Promise<PlaceOrderResponse> {
    const payload = this.buildOrderPayload(params);
    console.log("placeOrderLegacy payload", payload);
    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const timestamp = Math.floor(Date.now() / 1000) - 5;
    const isBuy = params.side.toUpperCase() === "BUY";
    const orderType = params.orderType.toUpperCase();
    const apiType =
      orderType === "MARKET" ? "ORDER_TYPE_MARKET" : "ORDER_TYPE_LIMIT";

    const orderReq: Record<string, any> = {
      address: this.getWalletAddress(),
      market_index: params.marketId,
      bucket_id: params.bucketId,
      price: completeMessage.price,
      quantity: completeMessage.quantity,
      type: apiType,
      signature,
      nonce: completeMessage.nonce,
      deadline: completeMessage.deadline,
      timestamp,
      asset_pair: params.marketId,
      market_id: params.marketId,
      size: completeMessage.quantity,
      is_buy: isBuy,
      order_type: orderType,
      client_order_id: `order-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
      time_in_force: params.timeInForce || "GTC",
    };
    if (params.leverage) orderReq.leverage = params.leverage;
    if (params.takeProfit) orderReq.take_profit = params.takeProfit;
    if (params.stopLoss) orderReq.stop_loss = params.stopLoss;
    if (params.postOnly) orderReq.post_only = true;
    if (params.hidden) orderReq.hidden = true;
    if (params.displayQuantity) orderReq.display_quantity = params.displayQuantity;
    if (params.reduceOnly) orderReq.reduce_only = true;

    const response = await this.httpClient.post("/clob/v1/order", orderReq);
    const body = response.body as PlaceOrderResponse & { order_id?: string; txhash?: string };
    const orderId = body.orderId;
    const txhash = body.txhash != null ? String(body.txhash) : (body as any).txHash;
    return {
      ...body,
      orderId: orderId ?? body.orderId,
      ...(txhash != null && { txhash }),
    } as PlaceOrderResponse;
  }

  /**
   * Place an order. Default: POST /tx/v1/submit (E2E/consensus). Set params.useLegacyClobOrder for POST /clob/v1/order.
   *
   * @param params - Order parameters (price/quantity converted to satoshi in payload builders)
   * @returns API response
   */
  async placeOrder(params: PlaceOrderParams): Promise<PlaceOrderResponse> {
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }
    if (
      !params.side ||
      (params.side.toUpperCase() !== "BUY" &&
        params.side.toUpperCase() !== "SELL")
    ) {
      throw new Error("side must be BUY or SELL");
    }
    if (
      !params.orderType ||
      (params.orderType.toUpperCase() !== "LIMIT" &&
        params.orderType.toUpperCase() !== "MARKET")
    ) {
      throw new Error("orderType must be LIMIT or MARKET");
    }
    const isMarket = params.orderType.toUpperCase() === "MARKET";
    if (!isMarket && (!params.price || params.price <= 0)) {
      throw new Error("price must be positive for non-market orders");
    }
    if (!params.quantity || params.quantity <= 0) {
      throw new Error("quantity must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    if (params.useLegacyClobOrder) {
      return this.placeOrderLegacy(params);
    }
    return this.placeOrderViaTx(params);
  }

  /**
   * Place multiple orders in a single batch request
   * POST /clob/v1/orders/batch
   * Supports 1-100 orders per batch
   *
   * @param params - Batch order parameters
   * @returns Batch order response with success/failure for each order
   */
  async placeBatchOrders(
    params: BatchOrderParams,
  ): Promise<BatchOrderResponse> {
    // Input validation
    if (!params.orders || !Array.isArray(params.orders)) {
      throw new Error("orders must be a non-empty array");
    }
    if (params.orders.length === 0) {
      throw new Error("At least one order is required");
    }
    if (params.orders.length > 100) {
      throw new Error("Maximum 100 orders per batch");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const timestamp = Math.floor(Date.now() / 1000) - 5; // 5 seconds in the past for clock skew

    // Build batch orders array
    const batchOrders = await Promise.all(
      params.orders.map(async (orderParams, index) => {
        // Validate each order
        if (!orderParams.marketId || orderParams.marketId.trim() === "") {
          throw new Error(`Order ${index}: marketId cannot be empty`);
        }
        if (!orderParams.bucketId || orderParams.bucketId.trim() === "") {
          throw new Error(`Order ${index}: bucketId cannot be empty`);
        }
        if (
          !orderParams.side ||
          (orderParams.side.toUpperCase() !== "BUY" &&
            orderParams.side.toUpperCase() !== "SELL")
        ) {
          throw new Error(`Order ${index}: side must be BUY or SELL`);
        }
        if (
          !orderParams.orderType ||
          (orderParams.orderType.toUpperCase() !== "LIMIT" &&
            orderParams.orderType.toUpperCase() !== "MARKET")
        ) {
          throw new Error(`Order ${index}: orderType must be LIMIT or MARKET`);
        }
        if (!orderParams.price || orderParams.price <= 0) {
          throw new Error(`Order ${index}: price must be positive`);
        }
        if (!orderParams.quantity || orderParams.quantity <= 0) {
          throw new Error(`Order ${index}: quantity must be positive`);
        }

        // Build order payload (same as placeOrder)
        const payload = this.buildOrderPayload(orderParams);
        const signatureBuilder = this.getSignatureBuilder();
        const completeMessage = await signatureBuilder.buildCompleteMessage(
          payload,
          this.getNonceProvider(),
        );
        const signature = await signatureBuilder.signPayload(completeMessage);

        const isBuy = orderParams.side.toUpperCase() === "BUY";
        const orderType = orderParams.orderType.toUpperCase();
        const apiType =
          orderType === "MARKET" ? "ORDER_TYPE_MARKET" : "ORDER_TYPE_LIMIT";
        const clientOrderId = `batch-${Date.now()}-${index}-${Math.floor(Math.random() * 1000000)}`;
        const sizeOrQuantity = completeMessage.quantity;
        const sideStr = isBuy ? "BUY" : "SELL";

        // Align with e2e request shape: symbol, quantity, side, orderType (camelCase), bucketId (camelCase).
        // Keep size + is_buy for router batch handler (createPlaceOrderRequest) compatibility.
        const orderReq: Record<string, any> = {
          address: walletAddress,
          symbol: orderParams.marketId,
          bucketId: orderParams.bucketId,
          price: completeMessage.price,
          size: sizeOrQuantity,
          quantity: sizeOrQuantity,
          is_buy: isBuy,
          side: sideStr,
          orderType: orderType,
          type: apiType,
          signature: signature,
          nonce: completeMessage.nonce,
          deadline: completeMessage.deadline,
          timestamp: timestamp,
          client_order_id: clientOrderId,
        };

        // Add optional parameters (camelCase for router batch handler; snake_case for e2e parity)
        if (orderParams.leverage) {
          orderReq.leverage = orderParams.leverage;
        }
        if (orderParams.takeProfit) {
          orderReq.take_profit = orderParams.takeProfit;
        }
        if (orderParams.stopLoss) {
          orderReq.stop_loss = orderParams.stopLoss;
        }
        if (orderParams.postOnly) {
          orderReq.post_only = true;
          orderReq.postOnly = true;
        }
        if (orderParams.hidden) {
          orderReq.hidden = true;
        }
        if (orderParams.displayQuantity) {
          orderReq.display_quantity = orderParams.displayQuantity;
          orderReq.displayQuantity = orderParams.displayQuantity;
        }
        if (orderParams.reduceOnly) {
          orderReq.reduce_only = true;
        }

        return orderReq;
      }),
    );

    // Submit batch to /clob/v1/orders/batch endpoint
    const response = await this.httpClient.post("/clob/v1/orders/batch", {
      orders: batchOrders,
    });

    return response.body as BatchOrderResponse;
  }

  /**
   * Cancel an order using RESTful DELETE endpoint.
   * DELETE /clob/v1/order/{order_id}?address={address}.
   * E2E uses POST /tx/v1/submit with signed TxRequest instead; prefer cancelOrder() for alignment.
   *
   * @param orderId - Order ID to cancel
   * @param address - Optional wallet address (defaults to initialized wallet)
   * @returns Cancel order response
   */
  async cancelOrderRESTful(
    orderId: string,
    address?: string,
  ): Promise<CancelOrderResponse> {
    // Input validation
    if (!orderId || orderId === "") {
      throw new Error("orderId cannot be empty");
    }

    if (!this.walletAddress && !address) {
      await this.initialize();
    }

    const walletAddress = address || this.getWalletAddress();

    // DELETE /clob/v1/order/{order_id}?address={address}
    const response = await this.httpClient.delete(
      `/clob/v1/order/${encodeURIComponent(orderId)}?address=${encodeURIComponent(walletAddress)}`,
    );

    return response.body as CancelOrderResponse;
  }

  /**
   * Cancel an order via POST /tx/v1/submit (same ingress pipeline as E2E).
   * Builds Cosmos-style Tx with /clob.v1.MsgCancelOrderRequest (type 43), auth_info, signatures;
   * maps TxV1SubmitResponse (accepted, txhash, errorMessage) to CancelOrderResponse.
   * Commits nonce only when accepted === true. On nonce error (thrown) or errorMessage
   * "duplicate transaction": refreshes nonce and retries once.
   */
  async cancelOrder(params: CancelOrderParams): Promise<CancelOrderResponse> {
    if (!params.orderId || params.orderId === "") {
      throw new Error("orderId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const payload = {
      action: OpCancelOrder,
      owner: walletAddress,
      address: walletAddress,
      orderId: params.orderId,
      marketIndex: params.marketId,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);

    const timestamp = toRFC3339Nano();
    const deadlineNum = parseInt(String(completeMessage.deadline), 10);
    const deadlineRFC3339 = new Date(deadlineNum * 1000)
      .toISOString()
      .replace(/\.\d{3}Z$/, "Z");

    const clobMessage: Record<string, unknown> = {
      "@type": "/clob.v1.MsgCancelOrderRequest",
      type: 43,
      fromAddress: walletAddress,
      address: walletAddress,
      orderId: params.orderId,
      symbol: params.marketId,
      timestamp,
    };

    const sigBase64 = this.hexSignatureToBase64(signature);
    const txRequest = {
      tx: {
        body: {
          messages: [clobMessage],
          memo: `cancel_order_${params.orderId}_${Math.floor(Date.now() / 1000)}`,
          timeoutTimestamp: deadlineRFC3339,
        },
        auth_info: {
          signer_infos: [
            {
              nonce: parseInt(String(completeMessage.nonce), 10),
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    try {
      const response = await this.httpClient.post("/tx/v1/submit", txRequest);
      const body = parseTxV1SubmitResponse(response.body);
      const out = this.mapSubmitTxToCancelOrderResponse(body, params.orderId);

      // Nonce lifecycle is system-managed: the router ingress keeper commits
      // nonces after successful morphcore execution. Client does not track locally.

      // Duplicate transaction: retry once with a fresh server-assigned nonce
      if (
        !out.success &&
        isDuplicateTransactionError(out.errorMessage) &&
        this.walletAddress
      ) {
        console.log("[FiClient] Duplicate cancel detected, retrying with fresh nonce");
        const retryCompleteMessage =
          await signatureBuilder.buildCompleteMessage(
            payload,
            this.getNonceProvider(),
          );
        const retrySignature =
          await signatureBuilder.signPayload(retryCompleteMessage);
        const retryDeadlineNum = parseInt(
          String(retryCompleteMessage.deadline),
          10,
        );
        const retryDeadlineRFC3339 = new Date(retryDeadlineNum * 1000)
          .toISOString()
          .replace(/\.\d{3}Z$/, "Z");
        const retryMessage: Record<string, unknown> = {
          "@type": "/clob.v1.MsgCancelOrderRequest",
          type: 43,
          fromAddress: walletAddress,
          address: walletAddress,
          orderId: params.orderId,
          symbol: params.marketId,
          timestamp: toRFC3339Nano(),
        };
        const retryTxRequest = {
          tx: {
            body: {
              messages: [retryMessage],
              memo: `cancel_order_${params.orderId}_${Math.floor(Date.now() / 1000)}`,
              timeoutTimestamp: retryDeadlineRFC3339,
            },
            auth_info: {
              signer_infos: [
                {
                  nonce: parseInt(String(retryCompleteMessage.nonce), 10),
                  deadline: retryDeadlineNum,
                  timestamp: toRFC3339Nano(),
                },
              ],
            },
            signatures: [this.hexSignatureToBase64(retrySignature)],
          },
        };
        const retryResponse = await this.httpClient.post(
          "/tx/v1/submit",
          retryTxRequest,
        );
        const retryBody = parseTxV1SubmitResponse(retryResponse.body);
        return this.mapSubmitTxToCancelOrderResponse(
          retryBody,
          params.orderId,
        );
      }
      return out;
    } catch (error) {
      // Nonce errors: retry once with a fresh server-assigned nonce
      if (shouldRefreshNonce(error)) {
        console.log("[FiClient] Nonce error on cancelOrder, retrying with fresh nonce");
        const retryCompleteMessage =
          await signatureBuilder.buildCompleteMessage(
            payload,
            this.getNonceProvider(),
          );
        const retrySignature =
          await signatureBuilder.signPayload(retryCompleteMessage);
        const retryDeadlineNum = parseInt(
          String(retryCompleteMessage.deadline),
          10,
        );
        const retryDeadlineRFC3339 = new Date(retryDeadlineNum * 1000)
          .toISOString()
          .replace(/\.\d{3}Z$/, "Z");
        const retryMessage: Record<string, unknown> = {
          "@type": "/clob.v1.MsgCancelOrderRequest",
          type: 43,
          fromAddress: walletAddress,
          address: walletAddress,
          orderId: params.orderId,
          symbol: params.marketId,
          timestamp: toRFC3339Nano(),
        };
        const retryTxRequest = {
          tx: {
            body: {
              messages: [retryMessage],
              memo: `cancel_order_${params.orderId}_${Math.floor(Date.now() / 1000)}`,
              timeoutTimestamp: retryDeadlineRFC3339,
            },
            auth_info: {
              signer_infos: [
                {
                  nonce: parseInt(String(retryCompleteMessage.nonce), 10),
                  deadline: retryDeadlineNum,
                  timestamp: toRFC3339Nano(),
                },
              ],
            },
            signatures: [this.hexSignatureToBase64(retrySignature)],
          },
        };
        const retryResponse = await this.httpClient.post(
          "/tx/v1/submit",
          retryTxRequest,
        );
        const retryBody = parseTxV1SubmitResponse(retryResponse.body);
        return this.mapSubmitTxToCancelOrderResponse(
          retryBody,
          params.orderId,
        );
      }
      throw error;
    }
  }

  /**
   * Map TxV1SubmitResponse (POST /tx/v1/submit) to CancelOrderResponse.
   * Commit nonce only when accepted === true (caller does it).
   */
  private mapSubmitTxToCancelOrderResponse(
    body: TxV1SubmitResponse,
    orderId: string,
  ): CancelOrderResponse {
    const accepted = body.accepted;
    const txhash = body.txhash;
    const nowMs = Date.now();
    const resultTimestamp = {
      seconds: Math.floor(nowMs / 1000),
      nanos: (nowMs % 1000) * 1e6,
    };
    return {
      success: accepted,
      txhash,
      result: {
        orderId,
        success: accepted,
        timestamp: resultTimestamp,
      },
      timestamp: resultTimestamp,
      ...(body.errorMessage && { errorMessage: body.errorMessage }),
    } as CancelOrderResponse;
  }

  /**
   * Modify an order
   * Follows ModifyOrder pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async modifyOrder(params: ModifyOrderParams): Promise<ModifyOrderResponse> {
    // Input validation (following Go implementation)
    if (!params.orderId || params.orderId === "") {
      throw new Error("orderId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    // Validate price and quantity (already numbers, no conversion needed)
    if (!params.newPrice || params.newPrice <= 0) {
      throw new Error("newPrice must be positive");
    }
    if (!params.newQuantity || params.newQuantity <= 0) {
      throw new Error("newQuantity must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload aligned with e2e/Go: common/tx/clob BuildModifyOrderTx and EIP-712 use marketIndex (not marketId).
    // Router ClobModifyOrderHandler reads address/owner, orderId, newPrice, newQuantity; marketIndex is in signed message for type consistency.
    const payload: Record<string, any> = {
      action: OpModifyOrder,
      orderId: params.orderId,
      marketIndex: params.marketId, // EIP-712 and e2e use marketIndex; SDK params keep marketId for API consistency
      newPrice: toSatoshi(params.newPrice),
      newQuantity: toSatoshi(params.newQuantity),
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );

    // Generate signature using generateSingleSignature (fixes recovery ID issue)
    const signature = await signatureBuilder.signPayload(completeMessage);

    // Build EIP712Tx structure (following buildEIP712Tx pattern)
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Change leverage for a position.
   * Aligns with E2E flow: orchestrator/deployment/air/e2e_tests/bucket/change_leverage_e2e_test.go
   * and standards/tx/bucket/tx.go BuildChangeLeverageTx.
   *
   * Submits via POST /tx/v1/submit (ingress path) — same as E2E.
   * Message: @type, type, operation, fromAddress, address, positionId, bucketId, marketIndex, newLeverage, timestamp.
   */
  async changeLeverage(
    params: ChangeLeverageParams,
  ): Promise<ChangeLeverageResponse> {
    if (params.newLeverage === undefined) {
      throw new Error("newLeverage must be provided");
    }
    const hasMarket =
      params.marketIndex !== undefined ||
      (params.marketId != null && params.marketId.trim() !== "");
    if (!hasMarket) {
      throw new Error("marketIndex or marketId must be provided");
    }
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const address = this.getWalletAddress();
    const marketIndexNum =
      params.marketIndex !== undefined
        ? Number(params.marketIndex)
        : parseInt(params.marketId!.trim(), 10);
    const marketIndex = Number.isNaN(marketIndexNum) ? 0 : marketIndexNum;
    const newLeverageStr = toSatoshi(params.newLeverage);

    // EIP-712 payload for signing (matches standards/payload/bucket.go)
    // Bucket-scoped: bucketId + marketIndex required (no positionId)
    const payload: Record<string, unknown> = {
      action: OpChangeLeverage,
      address,
      bucketId: params.bucketId,
      marketIndex,
      newLeverage: newLeverageStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const deadlineOffset = 300; // 5 minutes, matches standards BuildChangeLeverageTx
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
      deadlineOffset,
    );
    const signature = await signatureBuilder.signPayload(completeMessage);

    const deadlineNum = parseInt(String(completeMessage.deadline), 10);
    const bucketMessage: Record<string, unknown> = {
      "@type": "/bucket.v1.MsgUpdatePositionLeverageRequest",
      type: 0,
      operation: "change_leverage",
      fromAddress: address,
      address,
      bucketId: params.bucketId.trim(),
      marketIndex,
      newLeverage: newLeverageStr,
      timestamp: toRFC3339Nano(),
    };

    const doSubmit = async (
      msg: Record<string, unknown>,
      sig: string,
      nonceVal: string,
      deadlineVal: number,
    ) => {
      const txReq: Record<string, unknown> = {
        tx: {
          body: {
            messages: [msg],
            memo: `change_leverage_${Date.now()}_${nonceVal}`,
            timeoutTimestamp: new Date(deadlineVal * 1000)
              .toISOString()
              .replace(/\.\d{3}Z$/, "Z"),
          },
          auth_info: {
            signer_infos: [
              {
                nonce: parseInt(nonceVal, 10),
                deadline: deadlineVal,
                timestamp: toRFC3339Nano(),
              },
            ],
          },
          signatures: [this.hexSignatureToBase64(sig)],
        },
      };
      if (params.bucketId?.trim()) {
        txReq.shard_hint = params.bucketId.trim();
      }
      const resp = await this.httpClient.post("/tx/v1/submit", txReq);
      return this.mapSubmitTxToChangeLeverageResponse(
        parseTxV1SubmitResponse(resp.body),
      );
    };

    try {
      const result = await doSubmit(
        bucketMessage,
        signature,
        String(completeMessage.nonce),
        deadlineNum,
      );

      if (
        !result.success &&
        isDuplicateTransactionError(result.errorMessage ?? result.error) &&
        this.walletAddress
      ) {
        console.log(
          "[FiClient] Duplicate changeLeverage detected, retrying with fresh nonce",
        );
        const retryMsg = await signatureBuilder.buildCompleteMessage(
          payload,
          this.getNonceProvider(),
          deadlineOffset,
        );
        const retrySig = await signatureBuilder.signPayload(retryMsg);
        const retryBucketMsg: Record<string, unknown> = {
          ...bucketMessage,
          timestamp: toRFC3339Nano(),
        };
        return doSubmit(
          retryBucketMsg,
          retrySig,
          String(retryMsg.nonce),
          parseInt(String(retryMsg.deadline), 10),
        );
      }
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return {
        success: false,
        txhash: "",
        error: msg,
        errorMessage: msg,
      } as ChangeLeverageResponse;
    }
  }

  /**
   * Map TxV1SubmitResponse (POST /tx/v1/submit) to ChangeLeverageResponse.
   */
  private mapSubmitTxToChangeLeverageResponse(body: TxV1SubmitResponse): ChangeLeverageResponse {
    const errorText = !body.accepted && body.errorMessage
      ? body.errorMessage
      : undefined;
    return {
      success: body.accepted,
      ...(body.txhash && { txhash: body.txhash }),
      ...(body.errorMessage && { errorMessage: body.errorMessage }),
      ...(errorText != null && { error: errorText }),
    } as ChangeLeverageResponse;
  }

  /**
   * Close a position via POST /tx/v1/submit (E2E path).
   * Aligns with orchestrator/deployment/air/e2e_tests/bucket/close_position_e2e_test.go
   * and standards/tx/bucket/tx.go BuildClosePositionTx.
   *
   * Message: @type, type, operation, fromAddress, address, positionId, marketIndex,
   * closeSize, marketPrice, timestamp (camelCase per proto MsgClosePositionRequest).
   *
   * EIP-712 payload: action, address, positionId, marketIndex, closeSize, marketPrice,
   * nonce, deadline (standards/payload/common_use.go).
   *
   * When bucketId is provided, sets shard_hint for multi-node routing (E2E line 468).
   * Response: accepted, txhash/tx_hash, errorMessage — mapped to ClosePositionResponse.
   */
  async closePosition(
    params: ClosePositionParams,
  ): Promise<ClosePositionResponse> {
    // Input validation
    if (!params.positionId || params.positionId.trim() === "") {
      throw new Error("positionId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (!this.walletAddress) {
      await this.initialize();
    }

    const address = this.getWalletAddress();

    // Fetch market price if not provided (required for close position)
    let marketPriceValue: number;
    if (params.marketPrice && params.marketPrice > 0) {
      marketPriceValue = params.marketPrice;
    } else {
      // Fetch current market price from ticker
      try {
        const ticker = await this.infoClient.getMarketStats(params.marketId);
        if (!ticker || !ticker.lastPrice) {
          throw new Error(
            "marketPrice is required and could not be fetched from ticker",
          );
        }
        // Convert from satoshi string to decimal number
        marketPriceValue = fromSatoshi(ticker.lastPrice);
      } catch (error) {
        throw new Error(
          `Failed to fetch market price: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    }

    // Convert market price to satoshi string
    const marketPrice = toSatoshi(marketPriceValue).toString();

    // Handle quantity: 0 or undefined means close full position (per Go test)
    // If quantity is provided and > 0, use it; otherwise use "0" for full close
    const closeSize =
      params.quantity && params.quantity > 0
        ? toSatoshi(params.quantity).toString()
        : "0"; // "0" means close full position per Go test (line 424, 429)

    // Build EIP-712 payload matching Go test structure exactly
    // EIP-712 schema: address, marketId, closeSize, marketPrice, nonce, deadline
    // NOTE: NO 'action' field in payload (domain "bucket::close_position" is handled by signing)
    // NOTE: NO 'owner' field - uses 'address' instead
    const marketIndex = parseInt(params.marketId, 10) || 0;
    const payload: Record<string, any> = {
      action: OpClosePosition,
      address: address,
      positionId: params.positionId,
      marketIndex,
      closeSize: closeSize,
      marketPrice: marketPrice,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const deadlineOffset = 300;
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
      deadlineOffset,
    );
    const signature = await signatureBuilder.signPayload(completeMessage);

    const deadlineNum = parseInt(String(completeMessage.deadline), 10);
    const bucketMessage: Record<string, unknown> = {
      "@type": "/bucket.v1.MsgClosePositionRequest",
      type: 0,
      operation: "close_position",
      fromAddress: address,
      address: address,
      positionId: params.positionId,
      marketIndex,
      timestamp: toRFC3339Nano(),
      closeSize,
      marketPrice: marketPrice,
    };

    const doClose = async (
      msg: Record<string, unknown>,
      sig: string,
      nonceVal: string,
      deadlineVal: number,
    ) => {
      const txReq: Record<string, unknown> = {
        tx: {
          body: {
            messages: [msg],
            memo: `close_position_${Date.now()}_${nonceVal}`,
            timeoutTimestamp: new Date(deadlineVal * 1000)
              .toISOString()
              .replace(/\.\d{3}Z$/, "Z"),
          },
          auth_info: {
            signer_infos: [
              {
                nonce: parseInt(nonceVal, 10),
                deadline: deadlineVal,
                timestamp: toRFC3339Nano(),
              },
            ],
          },
          signatures: [this.hexSignatureToBase64(sig)],
        },
      };
      if (params.bucketId?.trim()) {
        txReq.shard_hint = params.bucketId.trim();
      }
      const resp = await this.httpClient.post("/tx/v1/submit", txReq);
      return this.mapSubmitTxToClosePositionResponse(
        parseTxV1SubmitResponse(resp.body),
      );
    };

    try {
      const result = await doClose(
        bucketMessage,
        signature,
        String(completeMessage.nonce),
        deadlineNum,
      );

      if (
        !result.success &&
        isDuplicateTransactionError(result.errorMessage) &&
        this.walletAddress
      ) {
        console.log(
          "[FiClient] Duplicate closePosition detected, retrying with fresh nonce",
        );
        const retryMsg = await signatureBuilder.buildCompleteMessage(
          payload,
          this.getNonceProvider(),
          deadlineOffset,
        );
        const retrySig = await signatureBuilder.signPayload(retryMsg);
        const retryBucketMsg: Record<string, unknown> = {
          "@type": "/bucket.v1.MsgClosePositionRequest",
          type: 0,
          operation: "close_position",
          fromAddress: address,
          address: address,
          positionId: params.positionId,
          marketIndex,
          timestamp: toRFC3339Nano(),
          closeSize,
          marketPrice,
        };
        return doClose(
          retryBucketMsg,
          retrySig,
          String(retryMsg.nonce),
          parseInt(String(retryMsg.deadline), 10),
        );
      }
      return result;
    } catch (error) {
      if (shouldRefreshNonce(error)) {
        console.log(
          "[FiClient] Nonce error on closePosition, retrying with fresh nonce",
        );
        const retryMsg = await signatureBuilder.buildCompleteMessage(
          payload,
          this.getNonceProvider(),
          deadlineOffset,
        );
        const retrySig = await signatureBuilder.signPayload(retryMsg);
        const retryBucketMsg: Record<string, unknown> = {
          "@type": "/bucket.v1.MsgClosePositionRequest",
          type: 0,
          operation: "close_position",
          fromAddress: address,
          address: address,
          positionId: params.positionId,
          marketIndex,
          timestamp: toRFC3339Nano(),
          closeSize,
          marketPrice,
        };
        return doClose(
          retryBucketMsg,
          retrySig,
          String(retryMsg.nonce),
          parseInt(String(retryMsg.deadline), 10),
        );
      }
      throw error;
    }
  }

  /**
   * Map TxV1SubmitResponse (POST /tx/v1/submit) to ClosePositionResponse.
   */
  private mapSubmitTxToClosePositionResponse(body: TxV1SubmitResponse): ClosePositionResponse {
    return {
      success: body.accepted,
      ...(body.txhash && { txhash: body.txhash }),
      ...(body.errorMessage && { errorMessage: body.errorMessage }),
    } as ClosePositionResponse;
  }

  /**
   * Close a position via POST /router/v1/tx (EIP712Tx).
   * Payload is aligned with router ClosePositionHandler and E2E BuildClosePositionTx:
   * - marketIndex (number): router uses getInt64(payload, "marketIndex")
   * - closeSize, marketPrice as decimal numbers: router uses getFloat64 then DecimalToSatoshi
   * - timestamp (Unix seconds): required for MsgClosePositionRequest.Timestamp
   * E2E uses POST /tx/v1/submit with a full Tx (body.messages + auth_info + signatures);
   * this method keeps /router/v1/tx and fixes payload so the router handler receives correct types.
   */
  async closePositionEIP712Tx(
    params: ClosePositionParams,
  ): Promise<ClosePositionResponse> {
    if (!params.positionId || params.positionId.trim() === "") {
      throw new Error("positionId cannot be empty");
    }
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (!this.walletAddress) {
      await this.initialize();
    }

    const marketIndexNum = parseInt(params.marketId, 10);
    if (Number.isNaN(marketIndexNum) || marketIndexNum < 0) {
      throw new Error("marketId must be a non-negative numeric string");
    }

    // closeSize: 0 = full close (e2e-aligned); handler expects decimal (router converts to satoshi)
    const closeSizeDecimal =
      params.quantity != null && params.quantity > 0 ? params.quantity : 0;

    let marketPriceDecimal: number;
    if (params.marketPrice != null && params.marketPrice > 0) {
      marketPriceDecimal = params.marketPrice;
    } else {
      try {
        const ticker = await this.infoClient.getMarketStats(params.marketId);
        if (!ticker?.lastPrice) {
          throw new Error("marketPrice is required and could not be fetched from ticker");
        }
        marketPriceDecimal = fromSatoshi(ticker.lastPrice);
      } catch (e) {
        throw new Error(
          `Failed to resolve market price: ${e instanceof Error ? e.message : String(e)}`,
        );
      }
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    const payload: Record<string, unknown> = {
      action: OpClosePosition,
      address: this.getWalletAddress(),
      positionId: params.positionId,
      marketIndex: marketIndexNum,
      closeSize: closeSizeDecimal,
      marketPrice: marketPriceDecimal,
      timestamp: nowSeconds,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    const body = response.body as Record<string, unknown>;

    // Normalize to E2E response shape: accepted → success, txhash, errorMessage
    const accepted = body?.accepted === true;
    const txhash =
      (body?.txhash as string) ?? (body?.tx_hash as string) ?? "";
    const errorMessage =
      (body?.errorMessage as string) ?? (body?.error_message as string) ?? "";

    return {
      ...body,
      success: accepted,
      txhash,
      errorMessage: errorMessage || undefined,
    } as ClosePositionResponse;
  }

  /**
   * Swap tokens
   * Follows Swap pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async swap(params: SwapParams): Promise<any> {
    // Input validation
    if (!params.fromToken || params.fromToken === "") {
      throw new Error("fromToken cannot be empty");
    }
    if (!params.toToken || params.toToken === "") {
      throw new Error("toToken cannot be empty");
    }
    // Validate amounts (already numbers, no conversion needed)
    if (!params.amountIn || params.amountIn <= 0) {
      throw new Error("amountIn must be positive");
    }
    if (!params.minAmountOut || params.minAmountOut <= 0) {
      throw new Error("minAmountOut must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following CreateSwapPayload pattern
    // Convert amounts from decimal numbers to satoshi strings
    const payload: Record<string, any> = {
      action: OpSwap,
      fromToken: params.fromToken,
      toToken: params.toToken,
      amountIn: toSatoshi(params.amountIn), // Convert to satoshi string
      minAmountOut: toSatoshi(params.minAmountOut), // Convert to satoshi string
    };

    // Add optional path if provided
    if (params.path && params.path.length > 0) {
      payload.path = params.path;
    }

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Change margin ratio (market-level).
   * Aligns with standards: action market::change_marginRatio, payload per standards/payload/market.go EIP712Types
   * (action, owner, marketIndex, newInitialMarginRatio, newMaintenanceMarginRatio, reason, nonce, deadline).
   * Submits via POST /router/v1/tx. Note: router eip712req has no handler for this action yet; add one when backend supports it.
   */
  async changeMarginRatioEIP712Tx(
    params: ChangeMarginRatioParams,
  ): Promise<ChangeMarginRatioResponse> {
    if (!params.marketId || params.marketId.trim() === "") {
      throw new Error("marketId cannot be empty");
    }
    if (params.reason === undefined || params.reason.trim() === "") {
      throw new Error("reason cannot be empty");
    }
    const initialRatio =
      typeof params.newInitialMarginRatio === "number"
        ? params.newInitialMarginRatio
        : parseFloat(String(params.newInitialMarginRatio));
    const maintenanceRatio =
      typeof params.newMaintenanceMarginRatio === "number"
        ? params.newMaintenanceMarginRatio
        : parseFloat(String(params.newMaintenanceMarginRatio));
    if (Number.isNaN(initialRatio) || initialRatio < 0) {
      throw new Error("newInitialMarginRatio must be a non-negative number");
    }
    if (Number.isNaN(maintenanceRatio) || maintenanceRatio < 0) {
      throw new Error("newMaintenanceMarginRatio must be a non-negative number");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Payload matches standards payload/market.go EIP712Types: action, owner, marketIndex, newInitialMarginRatio, newMaintenanceMarginRatio, reason, nonce, deadline (owner/nonce/deadline added by buildCompleteMessage)
    const payload: Record<string, any> = {
      action: OpChangeMarginRatio,
      marketIndex: params.marketId,
      newInitialMarginRatio: toSatoshi(initialRatio),
      newMaintenanceMarginRatio: toSatoshi(maintenanceRatio),
      reason: params.reason,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Internal transfer between accounts
   * Follows InternTransfer pattern from dex_client.go
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   */
  async internTransferEIP712Tx(
    params: InternTransferParams,
  ): Promise<InternTransferResponse> {
    // Input validation
    if (!params.fromAccount || params.fromAccount === "") {
      throw new Error("fromAccount cannot be empty");
    }
    if (!params.toAccount || params.toAccount === "") {
      throw new Error("toAccount cannot be empty");
    }
    if (!params.tokenAddress || params.tokenAddress === "") {
      throw new Error("tokenAddress cannot be empty");
    }
    // Validate amount (already number, no conversion needed)
    if (!params.amount || params.amount <= 0) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Build payload following CreateInternTransferPayload pattern
    // Convert amount from decimal number to satoshi string
    const payload: Record<string, any> = {
      action: OpInternTransfer,
      fromAccount: params.fromAccount,
      toAccount: params.toAccount,
      amount: toSatoshi(params.amount), // Convert to satoshi string
      tokenAddress: params.tokenAddress,
      address: this.getWalletAddress(),
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Transfer margin between buckets.
   * E2E design: POST /tx/v1/submit (ingress gateway) with full TxRequest.
   * Routes through morphcore consensus pipeline → processBankGenericTransfer → bucketKeeper.TransferBetweenBuckets.
   *
   * Aligns with:
   *   - standards/tx/bucket/tx.go BuildTransferBetweenBucketsTx
   *   - orchestrator/deployment/air/e2e_tests/bucket/transfer_between_buckets_e2e_test.go
   *
   * @param params - Bucket transfer parameters
   * @returns API response
   */
  async bucketTransfer(
    params: BucketTransferParams,
  ): Promise<BucketTransferResponse> {
    if (!params.fromBucketId || params.fromBucketId.trim() === "") {
      throw new Error("fromBucketId cannot be empty");
    }
    if (!params.toBucketId || params.toBucketId.trim() === "") {
      throw new Error("toBucketId cannot be empty");
    }
    if (
      params.amount === undefined ||
      (typeof params.amount === "number" && params.amount <= 0)
    ) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const amountStr = toSatoshi(params.amount);

    const payload: Record<string, any> = {
      action: OpBankBucketTransfer,
      sourceBucketId: params.fromBucketId,
      targetBucketId: params.toBucketId,
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const deadlineOffset = 300;
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
      deadlineOffset,
    );

    const signature = await signatureBuilder.signPayload(completeMessage);

    const timestamp = toRFC3339Nano();
    const deadlineNum = parseInt(String(completeMessage.deadline), 10);

    const bucketMessage: Record<string, unknown> = {
      "@type": "/bucket.v1.MsgTransferBetweenBucketsRequest",
      type: 0,
      operation: "bucket_transfer",
      fromAddress: walletAddress,
      address: walletAddress,
      sourceBucketId: params.fromBucketId,
      targetBucketId: params.toBucketId,
      amount: amountStr,
      timestamp,
    };

    const sigBase64 = this.hexSignatureToBase64(signature);

    const txRequest = {
      tx: {
        body: {
          messages: [bucketMessage],
          memo: `bucket_transfer_${Date.now()}`,
          timeoutTimestamp: new Date(deadlineNum * 1000).toISOString().replace(/\.\d{3}Z$/, "Z"),
        },
        auth_info: {
          signer_infos: [
            {
              nonce: parseInt(String(completeMessage.nonce), 10),
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    const response = await this.httpClient.post("/tx/v1/submit", txRequest);
    const body = parseTxV1SubmitResponse(response.body);
    const result = this.mapSubmitTxToBucketTransferResponse(body);

    if (
      !result.success &&
      isDuplicateTransactionError(result.errorMessage) &&
      this.walletAddress
    ) {
      console.log("[FiClient] Duplicate bucketTransfer detected, retrying with fresh nonce");
      const retryMessage = await signatureBuilder.buildCompleteMessage(
        payload,
        this.getNonceProvider(),
        deadlineOffset,
      );
      const retrySignature = await signatureBuilder.signPayload(retryMessage);
      const retryDeadlineNum = parseInt(String(retryMessage.deadline), 10);
      const retryTxRequest = {
        tx: {
          body: {
            messages: [
              {
                "@type": "/bucket.v1.MsgTransferBetweenBucketsRequest",
                type: 0,
                operation: "bucket_transfer",
                fromAddress: walletAddress,
                address: walletAddress,
                sourceBucketId: params.fromBucketId,
                targetBucketId: params.toBucketId,
                amount: amountStr,
                timestamp: toRFC3339Nano(),
              },
            ],
            memo: `bucket_transfer_${Date.now()}`,
            timeoutTimestamp: new Date(retryDeadlineNum * 1000)
              .toISOString()
              .replace(/\.\d{3}Z$/, "Z"),
          },
          auth_info: {
            signer_infos: [
              {
                nonce: parseInt(String(retryMessage.nonce), 10),
                deadline: retryDeadlineNum,
                timestamp: toRFC3339Nano(),
              },
            ],
          },
          signatures: [this.hexSignatureToBase64(retrySignature)],
        },
      };
      const retryResponse = await this.httpClient.post(
        "/tx/v1/submit",
        retryTxRequest,
      );
      const retryBody = parseTxV1SubmitResponse(retryResponse.body);
      return this.mapSubmitTxToBucketTransferResponse(retryBody);
    }

    return result;
  }

  /**
   * Map /tx/v1/submit response to CreateBucketResponse.
   * Uses client-generated bucketId since consensus response does not include it.
   */
  private mapSubmitTxToCreateBucketResponse(
    body: TxV1SubmitResponse,
    bucketId: string,
  ): CreateBucketResponse {
    return {
      success: body.accepted,
      bucketId,
      message: body.accepted
        ? `${bucketId} margin bucket created successfully`
        : body.errorMessage || "Bucket creation failed",
      ...(body.txhash && { txhash: body.txhash }),
      ...(body.errorMessage && { errorMessage: body.errorMessage }),
    };
  }

  /**
   * Map TxV1SubmitResponse (POST /tx/v1/submit) to BucketTransferResponse.
   */
  private mapSubmitTxToBucketTransferResponse(body: TxV1SubmitResponse): BucketTransferResponse {
    return {
      success: body.accepted,
      ...(body.txhash && { txhash: body.txhash }),
      ...(body.errorMessage && { errorMessage: body.errorMessage }),
    } as BucketTransferResponse;
  }

  /**
   * Extract spot balance to bucket (bank → bucket).
   * E2E flow standard: ingress path only (router/docs/improvements/bank_transfer_to_bucket_signable_vs_http_body.md).
   * Signable message (EIP-712): action, owner, bucketId, assetIndex, amount, nonce, deadline — used only for signing/verification.
   * Tx body message: proto-only (address, bucketId, assetIndex, amount, timestamp) plus @type/operation; nonce/deadline live in AuthInfo, signature in TxRequest.signatures.
   *
   * @param params - Extract to bucket parameters
   * @returns API response
   */
  async extractToBucket(
    params: ExtractToBucketParams,
  ): Promise<ExtractToBucketResponse> {
    // Input validation
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }
    const assetIndex =
      params.assetIndex ??
      (params.assetId?.toUpperCase() === "USDC" ? 1 : undefined);
    if (assetIndex == null || assetIndex < 1) {
      throw new Error(
        "Provide assetIndex (e.g. 1 for USDC) or assetId 'USDC'",
      );
    }
    if (!params.amount || params.amount <= 0) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const amountStr = toSatoshi(params.amount);

    // Signable payload per standards: action, owner, bucketId, assetIndex, amount, nonce, deadline (owner/nonce/deadline added by buildCompleteMessage)
    const payload: Record<string, unknown> = {
      action: OpBankTransferToBucket,
      bucketId: params.bucketId,
      assetIndex: Number(assetIndex),
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const deadlineOffset = 300; // 5 minutes, matches common/tx/bucket BuildTransferToBucketTx
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
      deadlineOffset,
    );

    const signature = await signatureBuilder.signPayload(completeMessage);

    // Timestamp for proto message and auth_info (RFC3339-style; ingress expects it for verification)
    const timestamp = toRFC3339Nano();
    const deadlineNum = parseInt(String(completeMessage.deadline), 10);

    // Body message: proto-only (MsgTransferToBucketRequest). E2E design: type 0 routes to processBankGenericTransfer.
    const bankMessage: Record<string, unknown> = {
      "@type": "/bank.v1.MsgTransferToBucketRequest",
      type: 0,
      operation: "transfer_to_bucket",
      fromAddress: walletAddress,
      address: walletAddress,
      bucketId: params.bucketId,
      assetIndex: Number(assetIndex),
      amount: amountStr,
      timestamp,
    };

    // Ingress expects signatures as base64 (proto bytes); convert if hex
    const sigBase64 = this.hexSignatureToBase64(signature);

    const txRequest = {
      tx: {
        body: {
          messages: [bankMessage],
          memo: `transfer_to_bucket_${Math.floor(Date.now() / 1000)}_${completeMessage.nonce}`,
          timeoutTimestamp: new Date(deadlineNum * 1000).toISOString().replace(/\.\d{3}Z$/, "Z"),
        },
        auth_info: {
          signer_infos: [
            {
              nonce: parseInt(String(completeMessage.nonce), 10),
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    const response = await this.httpClient.post("/tx/v1/submit", txRequest);
    const body = parseTxV1SubmitResponse(response.body);
    const result = this.mapSubmitTxToExtractToBucketResponse(body);

    // Duplicate transaction: retry once with a fresh server-assigned nonce
    if (
      !result.success &&
      isDuplicateTransactionError(result.errorMessage) &&
      this.walletAddress
    ) {
      console.log("[FiClient] Duplicate extractToBucket detected, retrying with fresh nonce");
      const retryMessage = await signatureBuilder.buildCompleteMessage(
        payload,
        this.getNonceProvider(),
        deadlineOffset,
      );
      const retrySignature =
        await signatureBuilder.signPayload(retryMessage);
      const retryDeadlineNum = parseInt(String(retryMessage.deadline), 10);
      const retryTxRequest = {
        tx: {
          body: {
            messages: [
              {
                "@type": "/bank.v1.MsgTransferToBucketRequest",
                type: 0,
                operation: "transfer_to_bucket",
                fromAddress: walletAddress,
                address: walletAddress,
                bucketId: params.bucketId,
                assetIndex: Number(assetIndex),
                amount: amountStr,
                timestamp: toRFC3339Nano(),
              },
            ],
            memo: `transfer_to_bucket_${Math.floor(Date.now() / 1000)}_${retryMessage.nonce}`,
            timeoutTimestamp: new Date(retryDeadlineNum * 1000)
              .toISOString()
              .replace(/\.\d{3}Z$/, "Z"),
          },
          auth_info: {
            signer_infos: [
              {
                nonce: parseInt(String(retryMessage.nonce), 10),
                deadline: retryDeadlineNum,
                timestamp: toRFC3339Nano(),
              },
            ],
          },
          signatures: [this.hexSignatureToBase64(retrySignature)],
        },
      };
      const retryResponse = await this.httpClient.post(
        "/tx/v1/submit",
        retryTxRequest,
      );
        const retryBody = parseTxV1SubmitResponse(retryResponse.body);
        return this.mapSubmitTxToExtractToBucketResponse(retryBody);
    }

    return result;
  }

  /**
   * Map TxV1SubmitResponse (POST /tx/v1/submit) to ExtractToBucketResponse.
   * Canonical JSON field from router is txhash; SDK exposes it as txhash.
   */
  private mapSubmitTxToExtractToBucketResponse(body: TxV1SubmitResponse): ExtractToBucketResponse {
    const timestamp = toRFC3339Nano();
    return {
      success: body.accepted,
      ...(body.txhash && { txhash: body.txhash }),
      ...(body.errorMessage && { errorMessage: body.errorMessage }),
      timestamp,
    };
  }

  /**
   * Create a margin bucket. E2E design: POST /tx/v1/submit (ingress gateway) with full TxRequest.
   * Aligns with orchestrator/deployment/air/e2e_tests/bucket/bucket_create_e2e_test.go and
   * standards/tx/bucket/tx.go BuildCreateTx.
   *
   * - Signable payload (EIP-712): action, owner, collateralAssetIndex, initialMargin, marginType, nonce, deadline.
   * - Routes through morphcore consensus → processBankGenericTransfer → CreateBucketFromConsensus.
   * - Bucket is created on consensus node, so subsequent extractToBucket (also via /tx/v1/submit) finds it.
   */
  async createBucket(
    params: CreateBucketParams,
  ): Promise<CreateBucketResponse> {
    if (
      params.collateralAssetIndex === undefined ||
      typeof params.collateralAssetIndex !== "number" ||
      params.collateralAssetIndex < 0
    ) {
      throw new Error("collateralAssetIndex must be a non-negative number");
    }
    if (!params.marginType || params.marginType.trim() === "") {
      throw new Error("marginType cannot be empty");
    }
    const marginTypeNorm = params.marginType.toLowerCase();
    if (marginTypeNorm !== "cross" && marginTypeNorm !== "isolated") {
      throw new Error("marginType must be 'cross' or 'isolated'");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const initialMargin = params.initialMargin ?? "0";
    const timestampUnix = Math.floor(Date.now() / 1000);
    const timestamp = toRFC3339Nano();

    // Generate deterministic bucketId (matches standards/tx/bucket/tx.go: bucket-{address_suffix}-{timestamp})
    const addressSuffix = walletAddress.replace(/^0x/i, "").slice(0, 8);
    const bucketId =
      params.bucketId && params.bucketId.trim() !== ""
        ? params.bucketId
        : `bucket-${addressSuffix}-${timestampUnix}`;

    const bucketTypeEnum =
      marginTypeNorm === "isolated"
        ? "BUCKET_TYPE_ISOLATED"
        : "BUCKET_TYPE_CROSS";

    // Signable payload per standards: action, owner, collateralAssetIndex, initialMargin, marginType, nonce, deadline
    const payload: Record<string, unknown> = {
      action: OpCreateMarginBucket,
      owner: walletAddress,
      collateralAssetIndex: String(params.collateralAssetIndex),
      initialMargin,
      marginType: marginTypeNorm,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload as Record<string, any>,
      this.getNonceProvider(),
      300, // 5 minutes deadline
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const deadlineNum = parseInt(String(completeMessage.deadline), 10);

    // Message format matches standards/tx/bucket/tx.go BuildCreateTx
    const createMessage: Record<string, unknown> = {
      "@type": "/bucket.v1.MsgCreateBucketRequest",
      type: 0,
      operation: "create_bucket",
      fromAddress: walletAddress,
      address: walletAddress,
      bucketId,
      bucketType: bucketTypeEnum,
      collateralAssetIndex: params.collateralAssetIndex,
      initialMargin,
      marginType: marginTypeNorm,
      timestamp,
    };

    const sigBase64 = this.hexSignatureToBase64(signature);
    const txRequest = {
      tx: {
        body: {
          messages: [createMessage],
          memo: `bucket_create_${Date.now()}_${completeMessage.nonce}`,
          timeoutTimestamp: new Date(deadlineNum * 1000)
            .toISOString()
            .replace(/\.\d{3}Z$/, "Z"),
        },
        auth_info: {
          signer_infos: [
            {
              nonce: parseInt(String(completeMessage.nonce), 10),
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    const response = await this.httpClient.post("/tx/v1/submit", txRequest);
    const body = parseTxV1SubmitResponse(response.body);
    return this.mapSubmitTxToCreateBucketResponse(body, bucketId);
  }

  /**
   * Withdraw margin from bucket to bank (bucket → bank).
   * E2E design: POST /tx/v1/submit (ingress gateway) with full TxRequest.
   * Uses MsgTransferToBankRequest (bucket proto); routes through morphcore consensus → processBankGenericTransfer.
   *
   * Aligns with:
   *   - proto-lib/proto/bucket/v1/tx.proto (MsgTransferToBankRequest)
   *   - orchestrator/deployment/air/e2e_tests/bucket/transfer_to_bank_e2e_test.go
   */
  async withdrawFromBucket(
    params: WithdrawFromBucketParams,
  ): Promise<WithdrawFromBucketResponse> {
    if (!params.bucketId || params.bucketId.trim() === "") {
      throw new Error("bucketId cannot be empty");
    }
    const assetIndex =
      params.assetIndex ??
      (params.assetId?.toUpperCase() === "USDC" ? 1 : undefined);
    if (assetIndex == null || assetIndex < 1) {
      throw new Error(
        "Provide assetIndex (e.g. 1 for USDC) or assetId 'USDC'",
      );
    }
    if (
      params.amount === undefined ||
      (typeof params.amount === "number" && params.amount <= 0)
    ) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const amountStr = toSatoshi(params.amount);

    // E2E/standards: action must be bucket::transfer_to_bank (standards/tx/bucket/tx.go BuildTransferToBankTx)
    const payload: Record<string, any> = {
      action: OpBucketTransferToBank,
      bucketId: params.bucketId,
      assetIndex: Number(assetIndex),
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const deadlineOffset = 300;
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
      deadlineOffset,
    );

    const signature = await signatureBuilder.signPayload(completeMessage);

    const timestamp = toRFC3339Nano();
    const deadlineNum = parseInt(String(completeMessage.deadline), 10);

    const bankMessage: Record<string, unknown> = {
      "@type": "/bucket.v1.MsgTransferToBankRequest",
      type: 0,
      operation: "transfer_to_bank",
      fromAddress: walletAddress,
      address: walletAddress,
      bucketId: params.bucketId,
      assetIndex: Number(assetIndex),
      amount: amountStr,
      timestamp,
    };

    const sigBase64 = this.hexSignatureToBase64(signature);

    const txRequest = {
      tx: {
        body: {
          messages: [bankMessage],
          memo: `transfer_to_bank_${Math.floor(Date.now() / 1000)}_${completeMessage.nonce}`,
          timeoutTimestamp: new Date(deadlineNum * 1000).toISOString().replace(/\.\d{3}Z$/, "Z"),
        },
        auth_info: {
          signer_infos: [
            {
              nonce: parseInt(String(completeMessage.nonce), 10),
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    const response = await this.httpClient.post("/tx/v1/submit", txRequest);
    const body = parseTxV1SubmitResponse(response.body);
    const result = this.mapSubmitTxToWithdrawFromBucketResponse(body);

    if (
      !result.success &&
      isDuplicateTransactionError(result.errorMessage) &&
      this.walletAddress
    ) {
      console.log("[FiClient] Duplicate withdrawFromBucket detected, retrying with fresh nonce");
      const retryMessage = await signatureBuilder.buildCompleteMessage(
        payload,
        this.getNonceProvider(),
        deadlineOffset,
      );
      const retrySignature = await signatureBuilder.signPayload(retryMessage);
      const retryDeadlineNum = parseInt(String(retryMessage.deadline), 10);
      const retryTxRequest = {
        tx: {
          body: {
            messages: [
              {
                "@type": "/bucket.v1.MsgTransferToBankRequest",
                type: 0,
                operation: "transfer_to_bank",
                fromAddress: walletAddress,
                address: walletAddress,
                bucketId: params.bucketId,
                assetIndex: Number(assetIndex),
                amount: amountStr,
                timestamp: toRFC3339Nano(),
              },
            ],
            memo: `transfer_to_bank_${Math.floor(Date.now() / 1000)}_${retryMessage.nonce}`,
            timeoutTimestamp: new Date(retryDeadlineNum * 1000)
              .toISOString()
              .replace(/\.\d{3}Z$/, "Z"),
          },
          auth_info: {
            signer_infos: [
              {
                nonce: parseInt(String(retryMessage.nonce), 10),
                deadline: retryDeadlineNum,
                timestamp: toRFC3339Nano(),
              },
            ],
          },
          signatures: [this.hexSignatureToBase64(retrySignature)],
        },
      };
      const retryResponse = await this.httpClient.post(
        "/tx/v1/submit",
        retryTxRequest,
      );
        const retryBody = parseTxV1SubmitResponse(retryResponse.body);
        return this.mapSubmitTxToWithdrawFromBucketResponse(retryBody);
    }

    return result;
  }

  /**
   * Map TxV1SubmitResponse (POST /tx/v1/submit) to WithdrawFromBucketResponse.
   */
  private mapSubmitTxToWithdrawFromBucketResponse(body: TxV1SubmitResponse): WithdrawFromBucketResponse {
    return {
      success: body.accepted,
      ...(body.txhash && { txhash: body.txhash }),
      ...(body.errorMessage && { errorMessage: body.errorMessage }),
    } as WithdrawFromBucketResponse;
  }

  /**
   * Mint new tokens
   * Uses EIP712Tx structure for Router v1 unified write endpoint
   *
   * @param params - Bank mint parameters
   * @returns API response
   */
  async bankMint(params: BankMintParams): Promise<BankMintResponse> {
    // Input validation
    if (!params.assetId || params.assetId.trim() === "") {
      throw new Error("assetId cannot be empty");
    }
    if (
      params.amount === undefined ||
      (typeof params.amount === "number" && params.amount <= 0)
    ) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    // Convert amount from decimal number to satoshi string
    const amountStr = toSatoshi(params.amount);

    // Build payload following bank::mint EIP712 types
    // Payload uses: owner, address, asset, amount, nonce, deadline
    // address is required for signature verification
    const payload: Record<string, any> = {
      action: OpBankMint,
      address: this.getWalletAddress(), // Required for signature verification
      asset: params.assetId, // Use "asset" not "assetId" in payload
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
    );
    const signature = await signatureBuilder.signPayload(completeMessage);
    const eip712Tx = signatureBuilder.buildEIP712Tx(completeMessage, signature);

    // Submit to /router/v1/tx endpoint
    const response = await this.httpClient.post("/router/v1/tx", eip712Tx);
    return response.body;
  }

  /**
   * Spot-to-address transfer via POST /tx/v1/submit.
   * Aligns with standards/tx/bank/tx.go BuildTransferTx and
   * orchestrator/deployment/air/e2e_tests/bank/bank_transfer_e2e_test.go.
   *
   * EIP-712 payload: action, owner, toAddress, assetIndex, amount, nonce, deadline.
   * Body message: MsgTransferRequest (type 16) → processBankTransfer → SendCoins.
   *
   * @param params - Bank transfer parameters (to, assetIndex or assetId, amount)
   * @returns API response with success, txhash, errorMessage
   */
  async bankTransfer(
    params: BankTransferParams,
  ): Promise<BankTransferResponse> {
    if (!params.to || params.to.trim() === "") {
      throw new Error("to address cannot be empty");
    }
    const assetIndex = this.resolveBankTransferAssetIndex(params);
    if (assetIndex == null || assetIndex < 0) {
      throw new Error(
        "Provide assetIndex (e.g. 0 for MORM, 1 for USDC) or assetId (MORM, USDC, BTC)",
      );
    }
    if (!params.amount || params.amount <= 0) {
      throw new Error("amount must be positive");
    }

    if (!this.walletAddress) {
      await this.initialize();
    }

    const walletAddress = this.getWalletAddress();
    const amountStr = toSatoshi(params.amount);

    const payload: Record<string, unknown> = {
      action: OpBankTransfer,
      owner: walletAddress,
      toAddress: params.to,
      assetIndex: Number(assetIndex),
      amount: amountStr,
    };

    const signatureBuilder = this.getSignatureBuilder();
    const deadlineOffset = 300;
    const completeMessage = await signatureBuilder.buildCompleteMessage(
      payload,
      this.getNonceProvider(),
      deadlineOffset,
    );

    const signature = await signatureBuilder.signPayload(completeMessage);

    const timestamp = toRFC3339Nano();
    const deadlineNum = parseInt(String(completeMessage.deadline), 10);

    const bankMessage: Record<string, unknown> = {
      "@type": "/bank.v1.MsgTransferRequest",
      type: 16,
      fromAddress: walletAddress,
      toAddress: params.to,
      assetIndex: Number(assetIndex),
      amount: amountStr,
      timestamp,
    };
    if (params.memo) {
      bankMessage.memo = params.memo;
    }

    const sigBase64 = this.hexSignatureToBase64(signature);

    const txRequest = {
      tx: {
        body: {
          messages: [bankMessage],
          memo: `transfer_${params.to.slice(0, 8)}_${Date.now()}`,
          timeoutTimestamp: new Date(deadlineNum * 1000)
            .toISOString()
            .replace(/\.\d{3}Z$/, "Z"),
        },
        auth_info: {
          signer_infos: [
            {
              nonce: parseInt(String(completeMessage.nonce), 10),
              deadline: deadlineNum,
              timestamp,
            },
          ],
        },
        signatures: [sigBase64],
      },
    };

    const response = await this.httpClient.post("/tx/v1/submit", txRequest);
    const body = parseTxV1SubmitResponse(response.body);
    const result = this.mapSubmitTxToBankTransferResponse(body);

    if (
      !result.success &&
      isDuplicateTransactionError(result.errorMessage) &&
      this.walletAddress
    ) {
      console.log(
        "[FiClient] Duplicate bankTransfer detected, retrying with fresh nonce",
      );
      const retryMessage = await signatureBuilder.buildCompleteMessage(
        payload,
        this.getNonceProvider(),
        deadlineOffset,
      );
      const retrySignature = await signatureBuilder.signPayload(retryMessage);
      const retryDeadlineNum = parseInt(String(retryMessage.deadline), 10);
      const retryTxRequest = {
        tx: {
          body: {
            messages: [
              {
                "@type": "/bank.v1.MsgTransferRequest",
                type: 16,
                fromAddress: walletAddress,
                toAddress: params.to,
                assetIndex: Number(assetIndex),
                amount: amountStr,
                timestamp: toRFC3339Nano(),
                ...(params.memo && { memo: params.memo }),
              },
            ],
            memo: `transfer_${params.to.slice(0, 8)}_${Date.now()}`,
            timeoutTimestamp: new Date(retryDeadlineNum * 1000)
              .toISOString()
              .replace(/\.\d{3}Z$/, "Z"),
          },
          auth_info: {
            signer_infos: [
              {
                nonce: parseInt(String(retryMessage.nonce), 10),
                deadline: retryDeadlineNum,
                timestamp: toRFC3339Nano(),
              },
            ],
          },
          signatures: [this.hexSignatureToBase64(retrySignature)],
        },
      };
      const retryResponse = await this.httpClient.post(
        "/tx/v1/submit",
        retryTxRequest,
      );
        const retryBody = parseTxV1SubmitResponse(retryResponse.body);
        return this.mapSubmitTxToBankTransferResponse(retryBody);
    }

    return result;
  }

  /**
   * Resolve assetIndex from params. Prefer explicit assetIndex; fallback to assetId mapping.
   */
  private resolveBankTransferAssetIndex(params: BankTransferParams): number | null {
    if (params.assetIndex != null && params.assetIndex >= 0) {
      return params.assetIndex;
    }
    const id = params.assetId?.toUpperCase();
    if (id === "MORM") return 0;
    if (id === "USDC") return 1;
    if (id === "BTC") return 2;
    return null;
  }

  /**
   * Map TxV1SubmitResponse (POST /tx/v1/submit) to BankTransferResponse.
   */
  private mapSubmitTxToBankTransferResponse(body: TxV1SubmitResponse): BankTransferResponse {
    return {
      success: body.accepted,
      ...(body.txhash && { txhash: body.txhash }),
      ...(body.errorMessage && { errorMessage: body.errorMessage }),
    } as BankTransferResponse;
  }

  /**
   * Check position risk before opening/modifying
   * POST /bucket/v1/position/check-risk
   * Important for risk management before placing orders
   *
   * @param params - Position risk check parameters
   * @returns Risk assessment result
   */
  async checkPositionRisk(params: {
    address: string;
    market_index: string;
    side: string;
    size: string;
    leverage: string;
  }): Promise<{
    risk_level: string;
    liquidation_price: string;
    margin_required: string;
    can_open: boolean;
    warnings: string[];
  }> {
    // Input validation
    if (!params.address || params.address.trim() === "") {
      throw new Error("address cannot be empty");
    }
    if (!params.market_index || params.market_index.trim() === "") {
      throw new Error("market_index cannot be empty");
    }
    if (!params.side || (params.side.toUpperCase() !== "LONG" && params.side.toUpperCase() !== "SHORT")) {
      throw new Error("side must be LONG or SHORT");
    }
    if (!params.size || params.size.trim() === "") {
      throw new Error("size cannot be empty");
    }
    if (!params.leverage || params.leverage.trim() === "") {
      throw new Error("leverage cannot be empty");
    }

    const response = await this.httpClient.post(
      "/bucket/v1/position/check-risk",
      {
        address: params.address,
        market_index: params.market_index,
        side: params.side.toUpperCase(),
        size: params.size,
        leverage: params.leverage,
      },
    );

    return response.body;
  }
}
